import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MessageCircle, ShoppingBag, Star } from 'lucide-react';
import { ProductGallery } from '../components/ProductGallery';
import { AppointmentForm } from '../components/AppointmentForm';
import { ProductDetailSkeleton } from '../components/ProductDetailSkeleton';
import { ShareMenu } from '../components/ShareMenu';
import { StoryGenerator } from '../components/StoryGenerator';
import { useCurrency } from '../currency/CurrencyContext';
import { resolveLocalized } from '../lib/localizedProduct';
import { createAppointment, getProducts, resolveImageUrl } from '../lib/api';
import { AppointmentInput, Product, ProductVariant } from '../types';
import { ConflictError } from '../lib/errors';
import { useToast } from '../ui/UIContext';
import { useCart } from '../cart/CartContext';
import { ECOMMERCE_ENABLED } from '../App';
import { SEO } from '../seo/SEO';

const DEFAULT_WHATSAPP_NUMBER = '212600000000';
const SHARE_ORIGIN = 'https://faticaftan.com';

function sanitizeNumber(raw: string): string {
  return raw.replace(/[^\d]/g, '');
}

/**
 * Resolve a product image URL to an absolute https URL suitable for
 * Open Graph / Twitter Card preview crawlers. Backend-uploaded images
 * are stored as relative paths (`/images/<uuid>`) so we go through
 * resolveImageUrl to prepend the API origin; mock data already passes
 * absolute Unsplash URLs through unchanged. Returns undefined when the
 * resolution would produce a non-http URL — SEO.tsx will fall back to
 * the site default OG image.
 */
function absoluteOgImage(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const resolved = resolveImageUrl(raw);
  return /^https?:\/\//i.test(resolved) ? resolved : undefined;
}

export function ProductDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const showToast = useToast();
  const { addItem } = useCart();
  const { currency, format, formatMad } = useCurrency();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const data = await getProducts();
        if (!cancelled) setProducts(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to fetch products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const product = useMemo(() => products.find((p) => p.id === id), [products, id]);

  const handleSubmit = async (formData: AppointmentInput) => {
    try {
      await createAppointment(formData);
      showToast(t('form.success'), 'success');
      setScheduling(false);
    } catch (err) {
      if (err instanceof ConflictError) {
        showToast(t('form.errors.slotConflict'), 'error');
        return;
      }
      showToast(err instanceof Error ? err.message : 'Failed to schedule appointment', 'error');
    }
  };

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-red-700 font-display italic">{error}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-editorial mx-auto px-6 sm:px-10 py-24">
        <Link to="/" className="editorial-label text-ink-soft hover:text-brass inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('productDetail.back')}
        </Link>
        <p className="font-display text-3xl italic text-ash mt-12">{t('productDetail.notFound')}</p>
      </div>
    );
  }

  // Always render the primary image first, then dedup any extras from images[].
  // Without this, products created via the admin form (which keeps imageUrl
  // separate from images[]) would show only the secondaries here.
  const galleryImages = (() => {
    const primary = product.imageUrl;
    const extras = (product.images ?? []).filter((u) => u && u !== primary);
    return primary ? [primary, ...extras] : extras;
  })();
  // Title + description in the active UI language (falls back to source copy).
  const localized = resolveLocalized(product, i18n.language);
  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const isReadyToWear = product.productType === 'READY_TO_WEAR';
  // Sort variants by a canonical clothing-size order (XS → 4XL); unknown
  // sizes fall through to alphabetic so the picker never crashes on legacy data.
  const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
  const sortedVariants: ProductVariant[] = (product.variants ?? [])
    .slice()
    .sort((a, b) => {
      const ai = SIZE_ORDER.indexOf(a.size);
      const bi = SIZE_ORDER.indexOf(b.size);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.size.localeCompare(b.size);
    });
  const totalStock = sortedVariants.reduce((acc, v) => acc + v.stock, 0);
  const allOutOfStock = isReadyToWear && totalStock === 0;

  function handleAddToCart() {
    if (!product || !selectedSize) return;
    const variant = sortedVariants.find((v) => v.size === selectedSize);
    if (!variant || variant.stock <= 0) {
      showToast(t('productDetail.outOfStock'), 'error');
      return;
    }
    addItem({
      productId: product.id,
      title: product.title,
      imageUrl: product.imageUrl,
      unitPrice: product.price,
      size: selectedSize,
      quantity: 1,
    });
    showToast(t('cart.toast.added', { size: selectedSize }), 'success');
  }

  function handleAddAndGo() {
    handleAddToCart();
    if (selectedSize) navigate('/cart');
  }

  // Builds a wa.me deeplink with the product + selected size + price
  // pre-filled, used while the in-site cart/checkout is off (sans-AE mode).
  function buildReserveWhatsAppHref(): string {
    if (!product || !selectedSize) return '#';
    const envNumber = import.meta.env.VITE_WHATSAPP_NUMBER as string | undefined;
    const number = sanitizeNumber(envNumber || DEFAULT_WHATSAPP_NUMBER);
    const message = t('productDetail.reserveWhatsAppMessage', {
      // Title in the visitor's language (what they're viewing); price stays
      // MAD-canonical — the WhatsApp message never uses the selected currency.
      title: resolveLocalized(product, i18n.language).title,
      size: selectedSize,
      price: formatMad(product.price),
    });
    return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
  }

  return (
    <>
      <SEO
        title={localized.title}
        description={localized.description.slice(0, 160)}
        image={absoluteOgImage(product.imageUrl)}
        ogType="product"
      />
      <section className="max-w-editorial mx-auto px-6 sm:px-10 py-10">
        <Link to="/" className="editorial-label text-ink-soft hover:text-brass inline-flex items-center gap-2 mb-8">
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('productDetail.back')}
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
          <ProductGallery images={galleryImages} title={localized.title} />

          <div className="flex flex-col">
            <span className="editorial-label text-brass mb-3">
              — {t('productDetail.durationLabel')} · {product.measurementDuration}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink mb-6" dir="auto">
              {localized.title}
            </h1>

            <div className="flex items-center gap-2 mb-6">
              <Star className="w-4 h-4 fill-brass text-brass" aria-hidden="true" />
              <span className="font-display text-base text-ink">{product.rating.toFixed(1)}</span>
              <span className="editorial-label text-ink-soft ms-2">{t('productDetail.rating')}</span>
            </div>

            <div className="mb-8 pb-8 border-b border-linen">
              <div className="flex items-baseline gap-4">
                <span className="editorial-label text-ink-soft">{t('productDetail.from')}</span>
                <span className="font-display text-4xl text-ink">
                  {format(product.price)}
                </span>
                {hasDiscount && (
                  <span className="font-display text-2xl italic text-ash line-through">
                    {format(product.originalPrice!)}
                  </span>
                )}
              </div>
              {currency !== 'MAD' && (
                <p className="editorial-label text-ink-soft mt-2">
                  {t('currency.indicative')} · {formatMad(product.price)}
                </p>
              )}
            </div>

            <div className="mb-10">
              <span className="editorial-label text-ink-soft block mb-3">
                {t('productDetail.detailsTitle')}
              </span>
              <p className="font-body text-base text-ink-soft leading-relaxed" dir="auto">
                {localized.description}
              </p>
            </div>

            <div className="mb-10 flex flex-wrap items-center gap-3">
              <ShareMenu
                variant="full"
                url={`${SHARE_ORIGIN}/products/${product.id}`}
                title={localized.title}
                description={localized.description.slice(0, 200)}
                image={absoluteOgImage(product.imageUrl)}
              />
              {absoluteOgImage(product.imageUrl) && (
                <StoryGenerator
                  imageUrl={absoluteOgImage(product.imageUrl) as string}
                  title={localized.title}
                  price={formatMad(product.price)}
                  productUrl={`${SHARE_ORIGIN}/products/${product.id}`}
                />
              )}
            </div>

            {isReadyToWear ? (
              <div className="mt-auto space-y-6">
                <div>
                  <span className="editorial-label text-ink-soft block mb-3">
                    {t('productDetail.sizeLabel')}
                  </span>
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('productDetail.sizeLabel')}>
                    {sortedVariants.map((v) => {
                      const outOfStock = v.stock <= 0;
                      const active = selectedSize === v.size;
                      return (
                        <button
                          key={v.size}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          disabled={outOfStock}
                          onClick={() => setSelectedSize(v.size)}
                          className={
                            'min-w-[56px] px-4 py-2 border editorial-label transition-colors ' +
                            (outOfStock
                              ? 'border-linen text-ash line-through cursor-not-allowed'
                              : active
                                ? 'border-ink bg-ink text-ivory'
                                : 'border-linen text-ink hover:border-ink')
                          }
                        >
                          {v.size}
                        </button>
                      );
                    })}
                  </div>
                  {allOutOfStock && (
                    <p className="font-body text-sm text-red-700 mt-3">
                      {t('productDetail.outOfStock')}
                    </p>
                  )}
                </div>

                {ECOMMERCE_ENABLED ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={handleAddToCart}
                      disabled={!selectedSize || allOutOfStock}
                      className="group flex-1 inline-flex items-center justify-center gap-3 bg-ink text-ivory px-6 py-4 hover:bg-brass-dark transition-colors duration-300 disabled:bg-ash disabled:cursor-not-allowed"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      <span className="editorial-label text-ivory">
                        {t('productDetail.addToCart')}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={handleAddAndGo}
                      disabled={!selectedSize || allOutOfStock}
                      className="flex-1 inline-flex items-center justify-center gap-3 border border-ink text-ink px-6 py-4 hover:bg-ink hover:text-ivory transition-colors duration-300 disabled:border-ash disabled:text-ash disabled:cursor-not-allowed"
                    >
                      <span className="editorial-label">
                        {t('productDetail.buyNow')}
                      </span>
                    </button>
                  </div>
                ) : (
                  <>
                    <a
                      href={selectedSize && !allOutOfStock ? buildReserveWhatsAppHref() : '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-disabled={!selectedSize || allOutOfStock}
                      onClick={(e) => {
                        if (!selectedSize || allOutOfStock) {
                          e.preventDefault();
                        }
                      }}
                      className={
                        'inline-flex items-center justify-center gap-3 px-6 py-4 transition-colors duration-300 ' +
                        (!selectedSize || allOutOfStock
                          ? 'bg-ash text-ivory cursor-not-allowed pointer-events-none'
                          : 'cursor-pointer')
                      }
                      style={
                        !selectedSize || allOutOfStock
                          ? undefined
                          : { backgroundColor: '#25D366', color: '#FFFFFF' }
                      }
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span
                        className="editorial-label"
                        style={
                          !selectedSize || allOutOfStock ? undefined : { color: '#FFFFFF' }
                        }
                      >
                        {t('productDetail.reserveOnWhatsApp')}
                      </span>
                    </a>
                    <p className="editorial-label text-ink-soft text-center">
                      {t('productDetail.reserveOnWhatsAppHint')}
                    </p>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setScheduling(true)}
                className="group inline-flex items-center justify-between bg-ink text-ivory px-6 py-4 hover:bg-brass-dark transition-colors duration-300 mt-auto"
              >
                <span className="editorial-label text-ivory">
                  {t('productDetail.reserveCta')}
                </span>
                <span className="font-display text-xl ms-4 transition-transform group-hover:translate-x-1">
                  →
                </span>
              </button>
            )}
          </div>
        </div>
      </section>

      {scheduling && (
        <AppointmentForm
          product={product}
          onClose={() => setScheduling(false)}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}
